import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  Checkbox,
  FormGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
} from '@mui/material';
import { Save as SaveIcon, PlayArrow as TestIcon } from '@mui/icons-material';
import { adminAPI } from '../services/api';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Phoenix', label: 'Arizona (MST)' },
  { value: 'America/Anchorage', label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
];

const SupportLine = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [testResult, setTestResult] = useState(null);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getSupportLineSettings();
      setSettings(response.data.settings);
    } catch (err) {
      setError('Failed to load support line settings: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const response = await adminAPI.updateSupportLineSettings(settings);
      setSettings(response.data.settings);
      setSuccess('Support line settings saved successfully');
    } catch (err) {
      setError('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      setError('');
      const response = await adminAPI.testSupportLineSettings();
      setTestResult(response.data);
    } catch (err) {
      setError('Failed to test: ' + (err.response?.data?.error || err.message));
    } finally {
      setTesting(false);
    }
  };

  const handleDayToggle = (day) => {
    const days = settings.business_days || [];
    if (days.includes(day)) {
      setSettings({ ...settings, business_days: days.filter(d => d !== day) });
    } else {
      setSettings({ ...settings, business_days: [...days, day].sort() });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!settings) {
    return (
      <Box>
        <Alert severity="error">Failed to load support line settings. Run migration 053 first.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        Support Line
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Configure the support line call forwarding system for 330-530-9300.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Enable/Disable */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={settings.is_enabled}
              onChange={(e) => setSettings({ ...settings, is_enabled: e.target.checked })}
            />
          }
          label={
            <Box>
              <Typography fontWeight={500}>Support Line Enabled</Typography>
              <Typography variant="body2" color="text.secondary">
                When disabled, calls to the support line will not be answered.
              </Typography>
            </Box>
          }
        />
      </Paper>

      {/* Voicemail */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={settings.voicemail_enabled || false}
              onChange={(e) => setSettings({ ...settings, voicemail_enabled: e.target.checked })}
            />
          }
          label={
            <Box>
              <Typography fontWeight={500}>Voicemail Enabled</Typography>
              <Typography variant="body2" color="text.secondary">
                When enabled, callers can leave a voicemail after the after-hours message.
              </Typography>
            </Box>
          }
        />
      </Paper>

      {/* Forward Number */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Call Forwarding</Typography>
        <TextField
          fullWidth
          label="Forward Number"
          value={settings.forward_number || ''}
          onChange={(e) => setSettings({ ...settings, forward_number: e.target.value })}
          placeholder="+1XXXXXXXXXX"
          helperText="Phone number to forward calls to during business hours (E.164 format)"
          sx={{ mt: 1 }}
        />
      </Paper>

      {/* Greeting & After-Hours Messages */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Messages</Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Greeting Message"
          value={settings.greeting_message || ''}
          onChange={(e) => setSettings({ ...settings, greeting_message: e.target.value })}
          helperText="Played when a call is answered"
          sx={{ mt: 1, mb: 3 }}
        />
        <TextField
          fullWidth
          multiline
          rows={3}
          label="After-Hours Message"
          value={settings.after_hours_message || ''}
          onChange={(e) => setSettings({ ...settings, after_hours_message: e.target.value })}
          helperText="Played when a call comes in outside business hours"
        />
      </Paper>

      {/* Business Hours */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Business Hours</Typography>

        <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 3, flexWrap: 'wrap' }}>
          <TextField
            label="Start Time"
            type="time"
            value={(settings.business_hours_start || '09:00').substring(0, 5)}
            onChange={(e) => setSettings({ ...settings, business_hours_start: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <TextField
            label="End Time"
            type="time"
            value={(settings.business_hours_end || '17:00').substring(0, 5)}
            onChange={(e) => setSettings({ ...settings, business_hours_end: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Business Days</Typography>
        <FormGroup row>
          {DAY_LABELS.map((label, i) => (
            <FormControlLabel
              key={i}
              control={
                <Checkbox
                  checked={(settings.business_days || []).includes(i)}
                  onChange={() => handleDayToggle(i)}
                  size="small"
                />
              }
              label={label}
            />
          ))}
        </FormGroup>

        <Divider sx={{ my: 2 }} />

        <FormControl sx={{ minWidth: 250 }}>
          <InputLabel>Timezone</InputLabel>
          <Select
            value={settings.timezone || 'America/New_York'}
            label="Timezone"
            onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
          >
            {TIMEZONES.map(tz => (
              <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
        <Button
          variant="outlined"
          startIcon={testing ? <CircularProgress size={20} /> : <TestIcon />}
          onClick={handleTest}
          disabled={testing}
        >
          {testing ? 'Testing...' : 'Test Settings'}
        </Button>
      </Box>

      {/* Test Result */}
      {testResult && (
        <Alert
          severity={testResult.currentlyWithinBusinessHours ? 'success' : 'info'}
          sx={{ mb: 3 }}
          onClose={() => setTestResult(null)}
        >
          <Typography variant="subtitle2" gutterBottom>
            {testResult.currentlyWithinBusinessHours ? 'Currently WITHIN business hours' : 'Currently OUTSIDE business hours'}
          </Typography>
          <Typography variant="body2">
            <strong>Would play:</strong> {testResult.wouldPlay}
          </Typography>
          {testResult.wouldForwardTo && (
            <Typography variant="body2">
              <strong>Would forward to:</strong> {testResult.wouldForwardTo}
            </Typography>
          )}
          <Typography variant="body2">
            <strong>Enabled:</strong> {testResult.isEnabled ? 'Yes' : 'No'}
          </Typography>
          <Typography variant="body2">
            <strong>Hours:</strong> {testResult.businessHours} ({testResult.timezone})
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default SupportLine;
